import useModal from '@fipnooone/react-modal';
import cn from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Api } from '@.api';
import { useId } from '@.hooks';
import { Layout } from '@.layout';
import { Board } from '@.partials';
import { Auth, LinkHelper } from '@.services';
import { Button, FilterData } from '@.ui';
import { ModalConfirm, ModalMessage } from '@/Components/Modal/Dialogs';
import { PermissionProps } from '@/Types';
import { UserRoleEnum } from '@/Types/api/User';

import { createStationTechnicalModeTitle } from '../lib/createStationTechnicalModeTitle';
import { useStationTabs } from '../lib/useStationTabs';
import { stationDetailStoreSelectors } from '../model/StationDetailStore';
import { StationTechnicalModeEnum } from '../model/StationTechnicalModeEnum';
import S from './StationDetailPage.module.scss';

export const StationDetailPage: React.FC<PermissionProps> = (props) => {
    const station = stationDetailStoreSelectors.use.station();
    const stationTechnicalMode = stationDetailStoreSelectors.use.stationTechnicalMode();
    const isLoadingChangeStationMode = stationDetailStoreSelectors.use.isLoadingChangeStationMode();
    const iRomaStationWS = stationDetailStoreSelectors.use.iRomaStationWS();

    const loadStation = stationDetailStoreSelectors.use.loadStation();
    const restoreStation = stationDetailStoreSelectors.use.restoreStation();
    const deleteStation = stationDetailStoreSelectors.use.deleteStation();
    const connectToStationWS = stationDetailStoreSelectors.use.connectToStationWS();
    const disconnectFromStationWS = stationDetailStoreSelectors.use.disconnectFromStationWS();

    const [filterData, setFilters] = useState<FilterData | undefined>(undefined);
    const [modalContent, modalControls] = useModal();
    const auth = Auth.Use();
    const navigate = useNavigate();

    const stationId = useMemo(() => station?.id, [station?.id]);

    const isTechnicalModeFeatureAvailable = auth?.user?.is_super || auth?.user?.role?.key === UserRoleEnum.SERVICE_PROVIDER;

    const handleDestroy = useCallback(() => {
        if (!stationId) return;

        Api.stations()
            .destroy(stationId)
            .onSuccess(() => {
                navigate(LinkHelper.iRomaScentsStations);
            });
    }, [navigate, stationId]);

    const handleDestroyConfirm = useCallback(() => {
        modalControls.set(<ModalConfirm message="Do you really want to permanently delete this station?" onConfirm={handleDestroy} />);
    }, [handleDestroy, modalControls]);

    const loadStationByRouteId = useCallback(
        (routeId: number) => {
            if (station?.id === routeId) {
                return;
            }

            return loadStation(routeId);
        },
        [loadStation, station?.id]
    );

    useId(loadStationByRouteId);

    const handleFilterChange = (data: FilterData) => setFilters(data);

    const stationTabs = useStationTabs({
        filterData,
        station: station ?? undefined,
        logsFiltersOnChange: handleFilterChange,
        productsFiltersOnChange: handleFilterChange,
        statisticFiltersOnChange: handleFilterChange,
        permissions: props.permissions,
    });

    useEffect(() => {
        setFilters(undefined);
    }, [stationTabs.stationTabsPath]);

    const enableTechnicalMode = async () => {
        if (!station) {
            return;
        }

        if (!auth?.user) {
            throw new Error('Auth user object is empty');
        }

        try {
            await connectToStationWS(auth.user.tokens.access, station);
        } catch (error: any) {
            let message = 'Something went wrong, try again later';

            if ('error' in error) {
                message = error.error;
            }

            modalControls.set(<ModalMessage messageClassName={S.error} message={message} />);
        }
    };

    useEffect(() => {
        return () => {
            void disconnectFromStationWS();
        };
    }, [disconnectFromStationWS]);

    /**
     * Show modal error when disconnected from station.
     */
    useEffect(() => {
        if (!iRomaStationWS) {
            return;
        }

        const onDisconnected = () => {
            modalControls.set(
                <ModalMessage messageClassName={S.error} message="Station 2000P disconnected from the server (connection channel was deleted on the server)" />
            );
        };

        iRomaStationWS.onStationDisconnected(onDisconnected);
    }, [iRomaStationWS, modalControls]);

    const isButtonTechnicalModeDisabled = !station || isLoadingChangeStationMode || !isTechnicalModeFeatureAvailable;

    return (
        <Layout
            nobg
            name="station"
            breadcrumbs={[
                { name: 'Stations', link: LinkHelper.iRomaScentsStations },
                { name: station?.name ?? '', current: true },
            ]}
        >
            {modalContent}
            {station && (
                <Board className={S.station}>
                    <Board.Item head>
                        <h1 className={S.station__header}>
                            <div className={S.station__header__title}>
                                <span>{station.name}</span>
                                {isTechnicalModeFeatureAvailable && (
                                    <div className={S.station__header__title__status}>
                                        Station technical mode:{' '}
                                        <span
                                            className={cn({
                                                [S.station__header__title__status_red]: stationTechnicalMode === StationTechnicalModeEnum.OFF,
                                                [S.station__header__title__status_green]: stationTechnicalMode === StationTechnicalModeEnum.ON,
                                            })}
                                        >
                                            {createStationTechnicalModeTitle(stationTechnicalMode)}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div>
                                {isTechnicalModeFeatureAvailable && (
                                    <Button
                                        className={S.station__technical_button}
                                        disabled={isButtonTechnicalModeDisabled}
                                        color="transparent"
                                        onClick={stationTechnicalMode === StationTechnicalModeEnum.ON ? disconnectFromStationWS : enableTechnicalMode}
                                    >
                                        {stationTechnicalMode === StationTechnicalModeEnum.ON ? 'Exit device technical mode' : 'Enter device technical mode'}
                                    </Button>
                                )}
                                {auth?.user?.is_super && <Button color="transparent" text={'Permanently delete'} onClick={handleDestroyConfirm} />}

                                <Button
                                    color="brown"
                                    text={station?.deleted_at ? 'Restore' : 'Archive'}
                                    onClick={station?.deleted_at ? restoreStation : deleteStation}
                                    disabled={!props.permissions?.delete}
                                />

                                <Button
                                    color="brown"
                                    text="Edit"
                                    onClick={() => navigate(LinkHelper.iRomaScentsStationsEditId(station?.id))}
                                    disabled={!!station?.deleted_at || !props.permissions?.update}
                                />
                            </div>
                        </h1>

                        {stationTabs.stationTabsControls}

                        <div className={S.station__filters}>{stationTabs.stationTabsFilters}</div>
                    </Board.Item>

                    {stationTabs.stationTabsContent}
                </Board>
            )}
        </Layout>
    );
};
